/**
 * Mock hooks for @threekit-tools/treble
 *
 * Attribute data is loaded at runtime from the Vivid API via
 * `initAttributesFromAPI()`. The static metadata (PlayCanvas asset names,
 * colour hex codes, etc.) comes from `attributeMetadataMap.ts`.
 *
 * When an attribute is changed via the UI, the mock `setAttribute` also
 * calls window.configurator.setConfig() through the playcanvasConfig bridge.
 */

import React from 'react';
import {
  ATTR_TO_PC_KEY,
  setPlayCanvasAttribute,
  normalizeVividImageUrl,
  resetPlayCanvasToDefaults,
} from './playcanvasConfig';
import { jeepProductService } from '../configurator/jeep/productInstance';
import {
  getJeepVariantMetadata,
  type JeepVariantMetadata,
} from '../configurator/jeep/attributeMetadataMap';
import type {
  ProductDataAttributes,
  ProductAttributeValue,
} from '../services/productAttributes/types';

// ── Attribute type rules ──
// Jeep Type uses 'String', everything else is 'Asset'.
const STRING_TYPE_ATTRS = new Set(['Jeep Type']);

// ── Default first-value rules ──
// For some attributes the "first" value in the list should be the default,
// for others a specific variant name is the default.
const DEFAULT_OVERRIDES: Record<string, string> = {
  'Paint color': 'Red',
  'Light Bar': 'None',
  'Side Steps': 'None',
};

/**
 * Build a single Threekit-style mock attribute object from API data.
 */
function buildMockAttribute(
  attrName: string,
  apiAttr: ProductDataAttributes
): any {
  const isString = STRING_TYPE_ATTRS.has(attrName);

  const enabledVariants = apiAttr.values.filter((v) => v.enabled);

  const values = enabledVariants.map((variant) => {
    const meta: JeepVariantMetadata = getJeepVariantMetadata(attrName, variant.name);
    const base: any = {
      label: variant.name,
      value: meta.pcOption,
      metadata: {
        _id: meta.priceId ?? String(variant.id),
        option: meta.pcOption,
        asset: meta.pcAsset,
        // API image (populated when CMS has an uploaded image)
        // Normalize to CDN URL for better performance
        _img: normalizeVividImageUrl(variant.image) ?? undefined,
        ...meta.extra,
      },
    };
    if (!isString) {
      base.assetId = meta.assetId;
    }
    return base;
  });

  // Determine default value
  const defaultName = DEFAULT_OVERRIDES[attrName];
  const defaultEntry = defaultName
    ? values.find((v: any) => v.label === defaultName) ?? values[0]
    : values[0];

  if (!defaultEntry) {
    // No enabled variants — return empty attribute
    return {
      type: isString ? 'String' : 'Asset',
      value: isString ? '' : { assetId: '' },
      defaultValue: isString ? '' : { assetId: '' },
      values: [],
    };
  }

  const defaultVal = isString
    ? defaultEntry.value
    : { assetId: defaultEntry.assetId };

  return {
    type: isString ? 'String' : 'Asset',
    value: defaultVal,
    defaultValue: defaultVal,
    values,
  };
}

// ── Multi-product cache (populated by initAllProducts) ──
const productCache = new Map<number, Record<string, any>>();
let activeProductId: number | null = null;
let _initialized = false;

/**
 * Fetch and cache attributes for a single product.
 * Returns immediately if already cached.
 */
async function initProductAttributes(productId: number): Promise<void> {
  if (productCache.has(productId)) return;

  console.log(`[threekitMocks] Loading product ${productId} from Vivid API…`);
  const apiAttrs = await jeepProductService.getAttributes(productId);

  const built: Record<string, any> = {};
  for (const [name, data] of Object.entries(apiAttrs)) {
    built[name] = buildMockAttribute(name, data);
  }

  productCache.set(productId, built);
  console.log(
    `[threekitMocks] Product ${productId} loaded:`,
    Object.keys(built)
  );
}

/**
 * Load multiple products in parallel. Call once at app startup.
 */
export async function initAllProducts(productIds: number[]): Promise<void> {
  try {
    await Promise.all(productIds.map((id) => initProductAttributes(id)));

    // Set first product as active by default if none set
    if (activeProductId === null && productIds.length > 0) {
      activeProductId = productIds[0];
    }
    _initialized = true;

    console.log(
      '[threekitMocks] All products loaded. Active:',
      activeProductId
    );
  } catch (err) {
    console.error('[threekitMocks] Failed to load products from API:', err);
  }
}

/**
 * @deprecated Use initAllProducts() instead.
 * Kept for backward compatibility — loads default product only.
 */
export async function initAttributesFromAPI(): Promise<void> {
  return initAllProducts([jeepProductService.getProductId()]);
}

/**
 * Reset all attributes of a cached product back to their defaultValue.
 * This ensures no stale user selections carry over when switching jeeps.
 */
export function resetProductToDefaults(productId: number): void {
  const attrs = productCache.get(productId);
  if (!attrs) return;

  for (const [name, attr] of Object.entries(attrs) as [string, any][]) {
    if (attr && attr.defaultValue !== undefined) {
      attr.value = typeof attr.defaultValue === 'object'
        ? { ...attr.defaultValue }
        : attr.defaultValue;
    }
  }
  console.log(`[threekitMocks] Product ${productId} reset to defaults`);
}

/**
 * Switch the active product. All subsequent getMockAttributes() / useAttribute()
 * calls will return data for this product.
 * Also resets the target product's attributes to defaults so stale
 * selections don't carry over.
 */
export function setActiveProduct(productId: number): void {
  if (!productCache.has(productId)) {
    console.warn(
      `[threekitMocks] Product ${productId} not cached. Call initAllProducts first.`
    );
    return;
  }
  // Reset both current and target product to defaults
  if (activeProductId !== null && activeProductId !== productId) {
    resetProductToDefaults(activeProductId);
  }
  resetProductToDefaults(productId);
  activeProductId = productId;
  // Push defaults to PlayCanvas 3D scene
  resetPlayCanvasToDefaults();
  console.log(`[threekitMocks] Active product set to ${productId} (reset to defaults)`);
}

/** Get the currently active product ID. */
export function getActiveProductId(): number | null {
  return activeProductId;
}

/** Whether `initAllProducts` has completed successfully. */
export function isAttributesInitialized(): boolean {
  return _initialized;
}

/** Direct access — returns attributes for the active product. */
export function getMockAttributes(): Record<string, any> {
  if (activeProductId === null) return {};
  return productCache.get(activeProductId) ?? {};
}

// ── Mock useAttribute hook ──

export const useAttribute = (attributeName: string) => {
  const attrs = getMockAttributes();
  const attribute = attrs[attributeName] || {
    type: 'String',
    value: '',
    defaultValue: '',
    values: [],
  };

  const setAttribute = (newValue: any) => {
    console.log(`Setting ${attributeName} to:`, newValue);

    // ── 1. Update the local mock state ──
    if (attribute.type === 'Asset') {
      attribute.value = { assetId: newValue };
    } else {
      attribute.value = newValue;
    }

    // ── 2. Forward to PlayCanvas configurator ──
    let pcOptionValue: any = newValue;
    let label: string | undefined;

    if (attribute.type === 'Asset') {
      const matched = attribute.values.find(
        (v: any) => v.assetId === newValue
      );
      if (matched) {
        pcOptionValue = matched.value ?? matched.metadata?.option ?? newValue;
        label = matched.label;
      }
    } else {
      const matched = attribute.values.find(
        (v: any) => v.value === newValue
      );
      if (matched) {
        label = matched.label;
      }
      pcOptionValue = newValue;
    }

    setPlayCanvasAttribute(attributeName, pcOptionValue, label);
  };

  return [attribute, setAttribute];
};

// ── Mock useConfigurator hook ──

export const useConfigurator = () => {
  const mock = getMockAttributes();
  const attributes: Record<string, any> = {};
  for (const key of Object.keys(mock)) {
    attributes[key] = mock[key]?.value;
  }
  // Legacy alias
  if (mock['Front Fender']) {
    attributes['Bumper Width'] = mock['Front Fender'].value;
  }

  const setConfiguration = (newConfig: any) => {
    console.log('Setting configuration:', newConfig);
    Object.assign(attributes, newConfig);

    // Forward bulk config to PlayCanvas
    const configurator = (window as any).configurator;
    if (configurator?.setConfig) {
      const pcPatch: Record<string, any> = {};
      for (const [uiKey, uiVal] of Object.entries(newConfig)) {
        const pcKey = ATTR_TO_PC_KEY[uiKey];
        if (pcKey) {
          pcPatch[pcKey] = uiVal;
        }
      }
      if (Object.keys(pcPatch).length > 0) {
        configurator.setConfig(pcPatch);
      }
    }
  };

  return [attributes, setConfiguration];
};

// ── Mock useThreekitInitStatus hook ──
export const useThreekitInitStatus = () => {
  return _initialized;
};

// Mock useProductCache hook
export const useProductCache = () => {
  const cache = {};
  const products = {};

  const handleLoadProduct = (productId: string) => {
    console.log('Loading product:', productId);
    return Promise.resolve();
  };

  return [{ cache, products }, handleLoadProduct];
};

// Mock usePlayerPortal hook
export const usePlayerPortal = () => {
  return null;
};

// Mock Share component
export const Share = () => null;

// Mock Player component
export const Player = () => null;

// Mock Snapshots component
export const Snapshots = () => null;

// Mock Wishlist component
export const Wishlist = () => null;

// Mock Zoom component
export const Zoom = () => null;

// Mock ProductLayout component
export const ProductLayout = ({ children }: any) => children;

// Mock CaretDownIcon component
export const CaretDownIcon = () =>
  React.createElement(
    'svg',
    {
      width: '12',
      height: '7',
      viewBox: '0 0 12 7',
      fill: 'currentColor',
    },
    React.createElement('path', {
      d: 'M1 1l5 5 5-5',
    })
  );
